import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../services/api';
import { Poster } from '../types';
import Preview from '../components/Preview';
import Button from '../components/ui/Button';
import FloatingActionButton from '../components/ai/FloatingActionButton';
import AISidebar from '../components/ai/AISidebar';

export default function View() {
  const { id } = useParams<{ id: string }>();
  const [poster, setPoster] = useState<Poster | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAISidebarOpen, setIsAISidebarOpen] = useState(false);

  useEffect(() => {
    if (id) {
      loadPoster(id);
    }
  }, [id]);

  const loadPoster = async (posterId: string) => {
    try {
      setLoading(true);
      const data = await api.getPoster(posterId);
      setPoster(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load poster');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-lg text-neutral-600">Loading poster...</p>
      </div>
    );
  }

  if (error || !poster) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <p className="text-lg text-red-600">{error || 'Poster not found'}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8 bg-neutral-50">
      <div className="fixed top-4 right-4 sm:top-6 sm:right-6 z-50">
        <Button
          onClick={handlePrint}
          className="shadow-lg"
        >
          Print / Save as PDF
        </Button>
      </div>

      <Preview poster={poster} />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 border-t border-neutral-200 print:hidden">
        <p className="text-xs text-neutral-400 text-center">
          Powered by{' '}
          <a
            href="https://teiko-labs.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-neutral-500 hover:text-neutral-600 transition-colors"
          >
            teiko-labs.com
          </a>
        </p>
      </div>

      {poster && (
        <>
          <FloatingActionButton
            onClick={() => setIsAISidebarOpen(true)}
            isOpen={isAISidebarOpen}
          />
          <AISidebar
            poster={poster}
            isOpen={isAISidebarOpen}
            onClose={() => setIsAISidebarOpen(false)}
          />
        </>
      )}

      <style>{`
        @media print {
          button {
            display: none !important;
          }
          body {
            background: white;
          }
        }
      `}</style>
    </div>
  );
}
